import React, { useState, useMemo } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, Car, Plane, Hotel, Users, AlertTriangle } from 'lucide-react';

const TRANSPORT_TYPES = [
  { value: 'car', label: 'Privatebil' },
  { value: 'taxi', label: 'Taxi' },
  { value: 'shuttle', label: 'Shuttle / Minibuss' },
  { value: 'bus', label: 'Buss' },
  { value: 'limo', label: 'Limousin' },
  { value: 'boat', label: 'Boat' },
  { value: 'train', label: 'Tog' },
  { value: 'flight', label: 'Fly' },
  { value: 'other', label: 'Other' },
];

const GROUPS = [
  { id: 'couple', label: 'The couple' },
  { id: 'party', label: 'Bryllupsfollowt' },
  { id: 'guests', label: 'Guests' },
  { id: 'vendors', label: 'Vendors' },
];

const defaultTransport = () => ({
  id: `tr-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
  group: 'couple', type: 'car', description: '', from: '', to: '',
  time: '', capacity: '', vendor: '', phone: '', price: '', notes: '',
});

const defaultAccommodation = () => ({
  id: `acc-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
  hotelName: '', address: '', phone: '', bookingRef: '',
  checkIn: '', checkOut: '', roomType: '', rooms: '',
  price: '', group: 'couple', contactPerson: '', notes: '',
});

const Inp = ({ label, value, onChange, placeholder, type = 'text', className = '' }) => (
  <div className={className}>
    <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase tracking-wide mb-0.5 block">{label}</label>
    <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
  </div>
);

export const TransportPlanner = ({ transport, onChange, guests }) => {
  const [expandedTransport, setExpandedTransport] = useState(null);
  const [expandedAccom, setExpandedAccom] = useState(null);

  const rides = transport?.rides || [];
  const accommodations = transport?.accommodations || [];

  const updateRides = (list) => onChange({ ...transport, rides: list });
  const updateAccom = (list) => onChange({ ...transport, accommodations: list });

  const addRide = () => { updateRides([...rides, defaultTransport()]); setExpandedTransport(rides.length); };
  const removeRide = (idx) => updateRides(rides.filter((_, i) => i !== idx));
  const updateRide = (idx, field, value) => {
    const updated = [...rides];
    updated[idx] = { ...updated[idx], [field]: value };
    updateRides(updated);
  };

  const addAccom = () => { updateAccom([...accommodations, defaultAccommodation()]); setExpandedAccom(accommodations.length); };
  const removeAccom = (idx) => updateAccom(accommodations.filter((_, i) => i !== idx));
  const updateAccomItem = (idx, field, value) => {
    const updated = [...accommodations];
    updated[idx] = { ...updated[idx], [field]: value };
    updateAccom(updated);
  };

  // Auto-identify guests with transport/hotel needs
  const guestsNeedingTransport = useMemo(() =>
    (guests || []).filter(g => g.transportNeed && g.name?.trim()), [guests]);
  const guestsNeedingHotel = useMemo(() =>
    (guests || []).filter(g => g.hotelNeed && g.name?.trim()), [guests]);

  const totalTransportCost = rides.reduce((s, r) => s + (parseFloat(r.price) || 0), 0);
  const totalAccomCost = accommodations.reduce((s, a) => s + (parseFloat(a.price) || 0) * (parseInt(a.rooms) || 1), 0);

  return (
    <div data-testid="transport-planner" className="space-y-4">
      <div>
        <h3 className="font-sans font-semibold text-stone-800 text-sm">Transport & Accommodation</h3>
        <p className="text-xs font-sans text-stone-500 mt-0.5">Planlegg logistikk for alle involverte</p>
        <p className="text-[10px] font-sans text-stone-400 mt-1 leading-relaxed">
          Guests who have marked transport or hotel needs in the guest list are shown here automatically. Add driving routes, parking and accommodation.
        </p>
      </div>

      {/* Guest needs alert */}
      {(guestsNeedingTransport.length > 0 || guestsNeedingHotel.length > 0) && (
        <div className="bg-blue-50 rounded-xl border border-blue-200/50 p-4 space-y-1.5">
          <p className="text-[10px] font-sans font-bold text-blue-700 uppercase">Fra the guest list</p>
          {guestsNeedingTransport.length > 0 && (
            <p className="text-xs font-sans text-blue-600">
              <Car className="w-3 h-3 inline mr-1" /> {guestsNeedingTransport.length} guest{guestsNeedingTransport.length > 1 ? 'er' : ''} trenger transport: {guestsNeedingTransport.map(g => g.name).join(', ')}
            </p>
          )}
          {guestsNeedingHotel.length > 0 && (
            <p className="text-xs font-sans text-blue-600">
              <Hotel className="w-3 h-3 inline mr-1" /> {guestsNeedingHotel.length} guest{guestsNeedingHotel.length > 1 ? 'er' : ''} trenger overnatting: {guestsNeedingHotel.map(g => g.name).join(', ')}
            </p>
          )}
        </div>
      )}

      {/* Transport section */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-sans font-bold text-stone-600 uppercase tracking-wide">Transport</h4>
          {totalTransportCost > 0 && <span className="text-[10px] font-sans text-primary font-bold">{totalTransportCost.toLocaleString('en-US')} kr</span>}
        </div>
        {rides.map((ride, idx) => (
          <div key={ride.id} className={`border rounded-xl transition-all ${expandedTransport === idx ? 'border-primary/30 bg-white' : 'border-stone-200'}`}>
            <div className="flex items-center gap-2 p-3 cursor-pointer" onClick={() => setExpandedTransport(expandedTransport === idx ? null : idx)}>
              <Car className="w-4 h-4 text-stone-400" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-sans font-medium text-stone-700 truncate">{ride.description || `${TRANSPORT_TYPES.find(t => t.value === ride.type)?.label || 'Transport'} — ${GROUPS.find(g => g.id === ride.group)?.label || ''}`}</p>
                <p className="text-[9px] font-sans text-stone-400">{ride.from ? `${ride.from} → ${ride.to}` : ''} {ride.time ? `kl. ${ride.time}` : ''}</p>
              </div>
              <button type="button" onClick={e => { e.stopPropagation(); removeRide(idx); }} className="text-stone-300 hover:text-red-500 p-1"><Trash2 className="w-3 h-3" /></button>
              {expandedTransport === idx ? <ChevronUp className="w-3.5 h-3.5 text-stone-400" /> : <ChevronDown className="w-3.5 h-3.5 text-stone-400" />}
            </div>
            {expandedTransport === idx && (
              <div className="px-3 pb-3 border-t border-stone-100 space-y-2 pt-2">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <div>
                    <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase mb-0.5 block">Gruppe</label>
                    <select value={ride.group} onChange={e => updateRide(idx, 'group', e.target.value)}
                      className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none">
                      {GROUPS.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase mb-0.5 block">Type</label>
                    <select value={ride.type} onChange={e => updateRide(idx, 'type', e.target.value)}
                      className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none">
                      {TRANSPORT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <Inp label="Description" value={ride.description} onChange={v => updateRide(idx, 'description', v)} placeholder="e.g. shuttle fra hotell" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Inp label="Fra" value={ride.from} onChange={v => updateRide(idx, 'from', v)} placeholder="Departurested" />
                  <Inp label="Til" value={ride.to} onChange={v => updateRide(idx, 'to', v)} placeholder="Arrivalsted" />
                  <Inp label="Time" value={ride.time} onChange={v => updateRide(idx, 'time', v)} placeholder="14:00" />
                  <Inp label="Kapasitet" value={ride.capacity} onChange={v => updateRide(idx, 'capacity', v)} type="number" placeholder="Ant." />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <Inp label="Vendor" value={ride.vendor} onChange={v => updateRide(idx, 'vendor', v)} placeholder="Firma/person" />
                  <Inp label="Phone" value={ride.phone} onChange={v => updateRide(idx, 'phone', v)} placeholder="Phone" />
                  <Inp label="Price (kr)" value={ride.price} onChange={v => updateRide(idx, 'price', v)} type="number" placeholder="0" />
                </div>
                <Inp label="Notes" value={ride.notes} onChange={v => updateRide(idx, 'notes', v)} placeholder="Evt. detaljer" />
              </div>
            )}
          </div>
        ))}
        <button type="button" onClick={addRide}
          className="w-full border-2 border-dashed border-stone-200 rounded-xl p-2.5 flex items-center justify-center gap-2 text-stone-400 hover:border-stone-300 hover:text-stone-500 transition-all text-xs font-sans font-medium">
          <Plus className="w-3.5 h-3.5" /> Add transport
        </button>
      </div>

      {/* Parking */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4 space-y-2">
        <h4 className="text-[10px] font-sans font-bold text-stone-500 uppercase tracking-wide">Parkering</h4>
        <div className="grid grid-cols-2 gap-2">
          <Inp label="Parkeringssted" value={transport?.parkingLocation} onChange={v => onChange({ ...transport, parkingLocation: v })} placeholder="Adresse / sted" />
          <Inp label="Number of seats" value={transport?.parkingCapacity} onChange={v => onChange({ ...transport, parkingCapacity: v })} type="number" placeholder="Number of" />
          <Inp label="Price" value={transport?.parkingPrice} onChange={v => onChange({ ...transport, parkingPrice: v })} placeholder="e.g. free / 200 kr" />
          <Inp label="Notes" value={transport?.parkingNotes} onChange={v => onChange({ ...transport, parkingNotes: v })} placeholder="Evt. tillegg" />
        </div>
      </div>

      {/* Accommodation section */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-sans font-bold text-stone-600 uppercase tracking-wide">Accommodation</h4>
          {totalAccomCost > 0 && <span className="text-[10px] font-sans text-primary font-bold">{totalAccomCost.toLocaleString('en-US')} kr</span>}
        </div>
        {accommodations.map((acc, idx) => (
          <div key={acc.id} className={`border rounded-xl transition-all ${expandedAccom === idx ? 'border-primary/30 bg-white' : 'border-stone-200'}`}>
            <div className="flex items-center gap-2 p-3 cursor-pointer" onClick={() => setExpandedAccom(expandedAccom === idx ? null : idx)}>
              <Hotel className="w-4 h-4 text-stone-400" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-sans font-medium text-stone-700 truncate">{acc.hotelName || 'Nytt hotell'}</p>
                <p className="text-[9px] font-sans text-stone-400">{acc.rooms ? `${acc.rooms} rom` : ''} {acc.checkIn && acc.checkOut ? `${acc.checkIn} → ${acc.checkOut}` : ''}</p>
              </div>
              <button type="button" onClick={e => { e.stopPropagation(); removeAccom(idx); }} className="text-stone-300 hover:text-red-500 p-1"><Trash2 className="w-3 h-3" /></button>
              {expandedAccom === idx ? <ChevronUp className="w-3.5 h-3.5 text-stone-400" /> : <ChevronDown className="w-3.5 h-3.5 text-stone-400" />}
            </div>
            {expandedAccom === idx && (
              <div className="px-3 pb-3 border-t border-stone-100 space-y-2 pt-2">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <Inp label="Hotellnavn" value={acc.hotelName} onChange={v => updateAccomItem(idx, 'hotelName', v)} placeholder="Navn" />
                  <Inp label="Adresse" value={acc.address} onChange={v => updateAccomItem(idx, 'address', v)} placeholder="Adresse" />
                  <Inp label="Phone" value={acc.phone} onChange={v => updateAccomItem(idx, 'phone', v)} placeholder="Phone" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Inp label="Inncheck" value={acc.checkIn} onChange={v => updateAccomItem(idx, 'checkIn', v)} type="date" />
                  <Inp label="Utcheck" value={acc.checkOut} onChange={v => updateAccomItem(idx, 'checkOut', v)} type="date" />
                  <Inp label="Romtype" value={acc.roomType} onChange={v => updateAccomItem(idx, 'roomType', v)} placeholder="Dobbel / Suite" />
                  <Inp label="Number of rom" value={acc.rooms} onChange={v => updateAccomItem(idx, 'rooms', v)} type="number" placeholder="1" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <Inp label="Price per room" value={acc.price} onChange={v => updateAccomItem(idx, 'price', v)} type="number" placeholder="0" />
                  <Inp label="Bookingreferanse" value={acc.bookingRef} onChange={v => updateAccomItem(idx, 'bookingRef', v)} placeholder="Ref." />
                  <div>
                    <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase mb-0.5 block">Gruppe</label>
                    <select value={acc.group} onChange={e => updateAccomItem(idx, 'group', e.target.value)}
                      className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none">
                      {GROUPS.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
                    </select>
                  </div>
                </div>
                <Inp label="Notes" value={acc.notes} onChange={v => updateAccomItem(idx, 'notes', v)} placeholder="Evt. detaljer" />
              </div>
            )}
          </div>
        ))}
        <button type="button" onClick={addAccom}
          className="w-full border-2 border-dashed border-stone-200 rounded-xl p-2.5 flex items-center justify-center gap-2 text-stone-400 hover:border-stone-300 hover:text-stone-500 transition-all text-xs font-sans font-medium">
          <Plus className="w-3.5 h-3.5" /> Add overnatting
        </button>
      </div>
    </div>
  );
};
